import { lazy, type ComponentType } from "react";

/**
 * Wraps React.lazy with one transient retry + a one-shot reload fallback when
 * a chunk URL no longer exists (typical after a deploy ships new chunk hashes
 * while a tab is still open). Prevents blank-screen "Failed to fetch
 * dynamically imported module" errors.
 */
const RELOAD_FLAG = "hibane_chunk_reload";

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch {
      // brief pause then retry once for transient network blips
      await new Promise((r) => setTimeout(r, 400));
      try {
        return await factory();
      } catch (err2) {
        const msg = String((err2 as Error)?.message ?? "");
        const isChunkErr =
          msg.includes("Failed to fetch dynamically imported module") ||
          msg.includes("Importing a module script failed") ||
          (err2 as { name?: string })?.name === "ChunkLoadError";

        if (
          isChunkErr &&
          typeof window !== "undefined" &&
          !sessionStorage.getItem(RELOAD_FLAG)
        ) {
          sessionStorage.setItem(RELOAD_FLAG, "1");
          window.location.reload();
          // never resolve so React doesn't render the error boundary
          return new Promise<{ default: T }>(() => {});
        }
        throw err2;
      }
    }
  });
}

/** Call once on app mount after a successful render to allow future auto-reloads. */
export function clearChunkReloadFlag() {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(RELOAD_FLAG);
  }
}
